import React, { useEffect, useState, useRef, useMemo } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { IImage } from 'tbk-components/src/components/Image';
import Modal from 'tbk-components/src/components/Modal';
import placeholder from '../../../assets/images/video-placeholder.jpg';

export interface IVideo {
	src: string;
}

/**
 * @usePostData
 * @post_type video
 * @forceLinkedData
 */
export interface IVideoPostProps {
	post_title?: string;
	video_link?: string;
}

export interface IVideoModalProps extends IBaseElementProps {
	/**
	 * Fetch the video post from Resources Centre.
	 * If oembed is provided, this will be ignored.
	 */
	videoPost?: IVideoPostProps;
	title?: string;
	url?: IVideo;
	/**
	 * You can add "?autoplay=1" at the end of src to avoid clicking twice to play YouTube Videos (eg. https://www.youtube.com/embed/rdwz7QiG0lk?autoplay=1).
	 * @control textarea
	 */
	oembed?: string;
	poster?: IImage;
	playLabel?: string;
	autoPlay?: boolean;
	style: 'modal' | 'inline';
}

export const getYoutubeIDFromURL = (url: string): string | boolean => {
	// https://stackoverflow.com/questions/3452546/how-do-i-get-the-youtube-video-id-from-a-url tsdorsey's answer
	var regExp =
		/(https?:\/\/)?(((m|www)\.)?(youtube(-nocookie)?|youtube.googleapis)\.com.*(v\/|v=|vi=|vi\/|e\/|embed\/|user\/.*\/u\/\d+\/)|youtu\.be\/)([_0-9a-z-]+)/i;
	var match = url.match(regExp);
	return match && match[8].length == 11 ? match[8] : false;
};

/**
 * Video Modal
 * @block
 */
const VideoModal: React.FC<IVideoModalProps> = ({
	id,
	className,
	classNames = [],
	videoPost,
	url,
	oembed,
	poster,
	playLabel = 'Play video',
	autoPlay = false,
	style = 'modal',
}) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [embedURL, setEmbedURL] = useState('');
	const [thumbURL, setThumbURL] = useState(poster ? poster.src : '');
	const [aspectRatio, setAspectRatio] = useState('16/9');
	const [oembedCode, setOembedCode] = useState('');
	const ref = useRef(null);

	useEffect(() => {
		/**
		 * If videoPost is provided, use the video_link to set the oembed code
		 * This will be ignored if oembed is provided
		 */
		if (videoPost && videoPost.video_link) {
			setOembedCode(videoPost.video_link);
		}

		if (oembed) {
			setOembedCode(oembed);
		}
	}, [oembed]);

	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		if (url && !oembedCode) {
			const video = ref.current! as HTMLVideoElement;
			video.addEventListener('play', () => {
				setIsPlaying(true);
			});

			video.addEventListener('pause', () => {
				setIsPlaying(false);
			});
		}
	}, [ref, url, oembedCode]);

	function checkImageURL(url, callback) {
		const img = new Image();
		img.onload = () => {
			// YouTube returns a 120x90 thumbnail if no thumbnail is available
			// We can use this to determine if the thumbnail is valid or not
			// If the thumbnail is 120x90, we can assume it's not valid
			if (img.width <= 120 && img.height <= 90) {
				callback(false);
			} else {
				callback(true);
			}
		};
		img.onerror = () => callback(false);
		img.src = url;
	}

	useEffect(() => {
		if (oembedCode) {
			const urlMatches = oembedCode!.match(/\bhttps?:\/\/\S+/gi);

			if (urlMatches && urlMatches.length > 0) {
				setEmbedURL(urlMatches[0]);

				if (
					embedURL.indexOf('youtube.com/') > -1 ||
					embedURL.indexOf('youtu.be/') > -1
				) {
					if (!thumbURL) {
						/**
						 * Grab the thumbnail from the YouTube API
						 */
						const ytImgURL = `https://i1.ytimg.com/vi/${getYoutubeIDFromURL(
							embedURL,
						)}/maxresdefault.jpg`;
						checkImageURL(ytImgURL, function (isValid) {
							if (isValid) {
								setThumbURL(ytImgURL);
							} else {
								setThumbURL(placeholder);
							}
						});
					}
				} else if (embedURL.indexOf('vimeo.com/') > -1) {
					if (!thumbURL) {
						const fetchURL = `https://vimeo.com/api/oembed.json?url=${embedURL}`;

						/**
						 * Grab the thumbnail and aspect ratio from the Vimeo API
						 */
						fetch(fetchURL)
							.then((response) => response.json())
							.then((data) => {
								setThumbURL(data.thumbnail_url);
								setAspectRatio(`${data.width}/${data.height}`);
							})
							.catch((error) =>
								console.error(
									'Error fetching thumbnail:',
									error,
								),
							);
					}
				}
			}
		}
	}, [oembedCode, embedURL, thumbURL]);

	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'video',
					isPlaying && 'video--playing',
					thumbURL && 'video--poster',
					...classNames,
				)
			}
			style={
				!isPlaying && thumbURL
					? {
							backgroundImage: `url("${thumbURL}")`,
							aspectRatio: aspectRatio,
					  }
					: {}
			}
		>
			{style &&
				style === 'modal' &&
				url &&
				url.src &&
				!oembedCode &&
				showModal && (
					<Modal
						className="calian-video-modal  modal--video"
						onCancel={() => setShowModal(false)}
						headerClassNames={[]}
						bodyClassNames={[]}
						showCancel={false}
						showSubmit={false}
					>
						<video
							ref={ref}
							className="video__player"
							controls
							autoPlay={autoPlay}
						>
							<source src={url.src} type="video/mp4" />
							Your browser does not support the video tag.
						</video>
					</Modal>
				)}

			{style &&
				style === 'inline' &&
				url &&
				url.src &&
				!oembedCode &&
				showModal && (
					<div className="calian-video-inline">
						<video
							ref={ref}
							className="video__player"
							controls
							autoPlay={autoPlay}
						>
							<source src={url.src} type="video/mp4" />
							Your browser does not support the video tag.
						</video>
					</div>
				)}

			{style && style === 'modal' && oembedCode && showModal && (
				<Modal
					className="calian-video-modal  modal--video"
					onCancel={() => setShowModal(false)}
					headerClassNames={[]}
					bodyClassNames={[]}
					showCancel={false}
					showSubmit={false}
				>
					<EmbedVideo oembed={oembedCode} embedURL={embedURL} />
				</Modal>
			)}

			{style && style === 'inline' && oembedCode && showModal && (
				<div className="calian-video-inline">
					<EmbedVideo oembed={oembedCode} embedURL={embedURL} />
				</div>
			)}

			{!isPlaying && (
				<button
					className="btn-play"
					onClick={() => {
						setShowModal(true);
						if (!oembedCode) {
							ref.current &&
								(ref.current! as HTMLVideoElement).play();
						}
					}}
				>
					{playLabel}
				</button>
			)}
		</div>
	);
};

const EmbedVideo = ({
	oembed,
	embedURL,
}: {
	oembed: string;
	embedURL: string;
}) => {
	if (oembed.includes('youtube') || oembed.includes('youtu.be')) {
		return (
			<div className="video__embed">
				<iframe
					width="560"
					height="315"
					src={`https://www.youtube.com/embed/${getYoutubeIDFromURL(
						embedURL,
					)}?autoplay=1`}
					title="YouTube video player"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					allowFullScreen
					tabIndex={0}
				></iframe>
			</div>
		);
	} else {
		return (
			<div
				className="video__embed"
				dangerouslySetInnerHTML={{
					__html: oembed,
				}}
			/>
		);
	}
};

export default VideoModal;
